import { createAction, props } from '@ngrx/store';
import { AccommodationType, AppMessageValue, AppMessages, ChatMessage, CorpMemberState, PostHistoryState, PpaModel, PpaReviewStats, SaleType, ChatListingRef } from '../app.state';

export const setCorpMemberProfileData = createAction(
  '[CorpMember] Set CorpMember Success',
  props<{ data: CorpMemberState }>()
);

// ??
export const updateCorpMemberProfile = createAction(
  '[CorpMember] Update Profile',
  props<{ data: CorpMemberState }>() // could also be part of the corpMemberState object
);

export const profileUpdateSuccess = createAction('[CorpMember] Profile Update Success', props<{data: any | CorpMemberState}>());
export const profileUpdateError = createAction('[CorpMember] Profile Update Error');


export const newSaleData = createAction(
  '[Feed] New Sale Posts',
  props<{ data: SaleType[] }>()
);

export const newAccommodationData = createAction(
  '[Feed] New Accommodation Posts',
  props<{ data: AccommodationType[] }>()
);

/**
 * Dispatched by components that need the feed populated. The effect handling
 * this decides whether to actually call the API, based on how fresh the
 * store's copy already is - this is what makes the feed cache-first rather
 * than every consumer re-fetching on mount.
 */
export const loadAllItems = createAction('[Feed] Load All Items');

export const loadAllItemsSuccess = createAction(
  '[Feed] Load All Items Success',
  props<{ sales: SaleType[]; accommodations: AccommodationType[]; fetchedAt: number }>()
);

export const loadAllItemsFailure = createAction(
  '[Feed] Load All Items Failure',
  props<{ error: any }>()
);

/**
 * Set the whole AppMessages state
 */
export const initializeMessages = createAction(
  '[Chat] Initialize New Message',
  props<any | AppMessages>(), // we can omit texts & unread_messages, since it's a new message - it's gonna be empty.
);

/**
 * Update part (or probably whole) of the AppMessages state
 */
export const updateMessages = createAction(
  '[Chat] Update App Messages',
  props<object | AppMessages>(),
);

/**
 * Starting a new chat that didn't exist before
 * Only pass the state code of who you want to start the message with.
 */
export const newMessage = createAction(
  '[Chat] Start New Message',
  props<Omit<AppMessageValue, 'texts' | 'unread_messages'> & {
    source?: 'accommodation' | 'sale' | 'profile'
    /** Typed into the message box for them to send or change (e.g. asking about a sale). */
    draft?: string
    /** The listing the chat is about - the first message sent is tagged with it. */
    listing?: ChatListingRef
  }>(), // we can omit texts & unread_messages, since it's a new message - it's gonna be empty.
);

/**
 * New message in an existing chat.
 */
export const newChatMessage = createAction(
  '[Chat] New Chat Message',
  props<ChatMessage>()
);


/**
 * When a user with unread message opens those messages.
 * 
 * corp_member_id is optional because we can pick it from local storage in the reducer
 */
export const updateReadReceipts = createAction(
  '[Chat] User has open/read a chat',
  props<{ room: string, corp_member_id?: string }>()
);


/**
 * The other person read the messages sent to them in a direct chat (the socket's messages_read) -
 * the sender's ✓ turns into ✓✓.
 */
export const messagesRead = createAction(
  '[Chat] Messages read',
  props<{ room: string, reader: string, read_at: string }>()
);


/**
 * Older messages fetched after scrolling to the top of a room's currently-loaded history.
 */
export const prependOlderMessages = createAction(
  '[Chat] Prepend Older Messages',
  props<{ room: string; messages: ChatMessage[] }>()
)

export const initializePostsHistory = createAction(
  '[Posts] Initialize',
  props<{ data: PostHistoryState }>(),
);

/** A post was deleted - by you, or (over the socket) by its poster. `id` is `${_type}_${id}`. */
export const deletePostedPost = createAction(
  '[Posts] Delete post',
  props<{ id: string }>(),
);

/** You undid deleting one of your posts; it's back in post history and the feed. */
export const postRestored = createAction(
  '[Posts] Restored',
  props<{ post: SaleType | AccommodationType }>(),
);

/** A page of GET /ppas arrived (the /ppa page). */
export const ppasLoaded = createAction(
  '[PPAs] Loaded',
  props<{ ppas: PpaModel[]; total: number }>(),
);

/** The next page of GET /ppas arrived ("Show more" on a phone): added after the ones shown. */
export const morePpasLoaded = createAction(
  '[PPAs] More loaded',
  props<{ ppas: PpaModel[]; total: number }>(),
);

/** A corp member added a PPA; it goes first in the list. */
export const ppaAdded = createAction(
  '[PPAs] Added',
  props<{ ppa: PpaModel }>(),
);

/** A review was posted; `stats` are the PPA's numbers with it counted. */
export const ppaReviewStatsUpdated = createAction(
  '[PPAs] Review Stats Updated',
  props<{ id: number; stats: PpaReviewStats }>(),
);

/**
 * Dispatched by pages that show sale cards. The effect fetches GET /sale/bookmarks once per
 * login (only when logged in and not loaded yet).
 */
export const loadSavedSales = createAction('[Sales] Load Saved');

/** The saved sale ids arrived - or `null` to forget them (logout). */
export const savedSalesLoaded = createAction(
  '[Sales] Saved Loaded',
  props<{ ids: number[] | null }>(),
);

/** The corp member saved (bookmarked) a sale, or removed it from saved. */
export const saleSaveChanged = createAction(
  '[Sales] Save Changed',
  props<{ id: number; saved: boolean }>(),
);

/**
 * Dispatched by house cards. The effect fetches GET /accommodation/bookmarks once per login (only when
 * logged in and not loaded yet) - like loadSavedSales.
 */
export const loadSavedAccommodations = createAction('[Accommodations] Load Saved');

/** The saved house ids arrived - or `null` to forget them (logout). */
export const savedAccommodationsLoaded = createAction(
  '[Accommodations] Saved Loaded',
  props<{ ids: number[] | null }>(),
);

/** The corp member saved a house, or removed it from saved. */
export const accommodationSaveChanged = createAction(
  '[Accommodations] Save Changed',
  props<{ id: number; saved: boolean }>(),
);

/** The seller marked a sale sold (`sold_at` set) or available again (null). */
export const saleSoldChanged = createAction(
  '[Sales] Sold Changed',
  props<{ id: number; sold_at: string | null }>(),
);
